import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import {
  AnalyzeResultElementFlippingType,
  SellingZoneData,
  SellingZoneDataBranchResult,
  SellingZoneDataBranchResultEmail,
  SellingZoneElement,
  SellingZoneGraphItemSelected,
  SellingZoneParticipant,
  SellingZoneTimelineChanged,
} from '@substratahq/selling-zone/typing/base';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  clearDetailedAnalysis,
  getDeatailedAnalysis,
  sendViewEmail,
  setCurrentBranch,
  setCurrentEmail,
  setGraphItems,
  setSelectedGraphItem,
} from '../../../store/deals/deals.actions';
import { CrmData } from './components/crmData/CrmData';
import { ProspectData } from './components/prospectData/ProspectData';
import { ThreadData } from './components/threadData/ThreadData';
import { EmailSection } from './components/emailSection/EmailSection';
import { DetailedViewHeader } from './components/header/DetailedViewHeader';
import {
  DetailedAnalysis,
  DetailedAnalysisEmail,
  DetailedAnalysisEmailParticipant,
} from '../../../store/deals/deals.types';
import { SellingZoneSkeleton } from './components/skeletons/SellingZoneSkeleton';
import { useMixPanel } from '../../../components/MixPanel/useMixPanel';
import { MyEngagementsTitle } from './components/myEngagementsTitle/MyEngagementsTitle';
import { SampleDataLabel } from '../DealsTable/components/sampleDataLabel/SampleDataLabel';

export const DetailedView: React.FC = (): JSX.Element => {
  const { user } = useSelector((state: RootState) => state.auth);
  const {
    detailedAnalysis,
    selectedBranch,
    selectedEmail,
    selectedGraphItem,
    isLoading,
    neverAnalyzed,
  } = useSelector((state: RootState) => state.deals);

  const { search, pathname } = useLocation();

  const messageIdFromQuery = new URLSearchParams(search).get('messageId');

  const { engagementId, branchId } = useParams<{
    engagementId: string;
    branchId: string;
  }>();

  const [
    elementFlippingType,
    setElementFlippingType,
  ] = useState<AnalyzeResultElementFlippingType>('none');

  const [firstReplyIndex, setFirstReplyIndex] = useState(0);

  const dispatch = useDispatch();

  const { trackEvent } = useMixPanel();

  // eslint-disable-next-line
  const sellingZoneRef: MutableRefObject<SellingZoneElement> = useRef<any>() as MutableRefObject<any>;

  const internalThreadReadMoreUrl =
    'https://help.substrata.me/en/articles/6632615-intra-organizational-communication';

  const sellingZoneReadMoreUrl =
    'https://help.substrata.me/en/articles/6519583-selling-zone-101';

  useEffect(() => {
    dispatch(clearDetailedAnalysis());
  }, []); // eslint-disable-line

  useEffect(() => {
    if (
      user?.orgId &&
      user?.userId &&
      engagementId?.length &&
      branchId?.length
    ) {
      dispatch(
        getDeatailedAnalysis(
          user?.orgId,
          user?.userId,
          engagementId,
          branchId,
          neverAnalyzed || pathname?.includes('example')
        )
      );
    } // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user && engagementId && selectedEmail?.messageId) {
      dispatch(
        sendViewEmail(
          user.orgId,
          user.userId,
          engagementId,
          selectedEmail?.messageId
        )
      );
    } // eslint-disable-next-line
  }, [selectedEmail]);

  const getSellingZoneParticipant = (
    participant: DetailedAnalysisEmailParticipant
  ): SellingZoneParticipant => {
    return {
      email: participant?.email,
      metadata: {
        name: participant?.metadata?.name,
        avatarSrc: participant?.metadata?.avatarSrc || '',
        linkedin: participant?.metadata?.linkedin || '',
      },
      isSender: participant?.isSender,
      isUser: participant?.isUser,
      numberEmailsInBranch: participant?.numberEmailsInBranch,
    };
  };

  const getSellingZoneEmail = (
    email: DetailedAnalysisEmail,
    theFirstReplyIndex: number
  ): SellingZoneDataBranchResultEmail => {
    const allParticipants = [
      getSellingZoneParticipant({
        ...email.sender,
        isSender: true,
        isUser: false,
        isUserDomain: false,
        numberEmailsInBranch: 0,
      }),
      ...email?.recipients?.map((el) =>
        getSellingZoneParticipant({
          ...el,
          isSender: true,
          isUser: false,
          isUserDomain: false,
          numberEmailsInBranch: 0,
        })
      ),
    ];

    return {
      platformMessageId: email?.platformMessageId,
      participants: {
        leftParticipant: getSellingZoneParticipant(
          email?.participants?.leftParticipant
        ),
        rightParticipant: getSellingZoneParticipant(
          email?.participants?.rightParticipant
        ),
      },
      sentAt: email?.sentAt,
      isDraft: false,
      scoreInBranch: email?.scoreInBranch,
      index: typeof email?.index === 'number' ? email?.index : undefined,
      allEmailParticipants: allParticipants,
      repliedEmailIndex: theFirstReplyIndex,
    };
  };

  const convertSellingZoneData = (analyzedData: DetailedAnalysis) => {
    const sellingZoneData: SellingZoneData = {
      platformMessageIdToAnalyze: messageIdFromQuery,
      branchIdToSelect: branchId,
      branchResults: [],
      isThreadInternal: analyzedData?.isThreadInternal,
    };
    if (analyzedData?.branch) {
      // eslint-disable-next-line
      for (const branch of [analyzedData?.branch]) {
        // branch.branchId = uuidv4();

        const sellingZoneBranch: SellingZoneDataBranchResult = {
          branchId: branch?.branchId,
          nextMove: {
            value: branch?.nextMove?.value,
          },
          emails: [],
          draft: null,
          participants: [],
        };

        const firstEmailSender: DetailedAnalysisEmailParticipant = branch
          ?.emails?.[0]?.participants?.leftParticipant?.isSender
          ? branch?.emails?.[0]?.participants?.leftParticipant
          : branch?.emails?.[0]?.participants?.rightParticipant;

        // eslint-disable-next-line
        for (const email of branch?.emails) {
          const repliedMessage:
            | DetailedAnalysisEmail
            | undefined = branch?.emails?.find((e) => {
            const senderPerson: DetailedAnalysisEmailParticipant = e
              .participants?.leftParticipant?.isSender
              ? e.participants?.leftParticipant
              : e.participants?.rightParticipant;

            return senderPerson?.email !== firstEmailSender?.email;
          });

          const repliedEmailIndex =
            repliedMessage?.index !== undefined &&
            typeof repliedMessage?.index === 'number'
              ? repliedMessage?.index
              : 10000;

          if (repliedEmailIndex !== 10000 && !firstReplyIndex) {
            setFirstReplyIndex(repliedEmailIndex);
          }

          sellingZoneBranch.emails.push(
            // eslint-desable-next-line
            getSellingZoneEmail(
              email,
              repliedEmailIndex
            ) as SellingZoneDataBranchResultEmail
          );
        }

        // eslint-disable-next-line
        for (const participant of branch?.mainBranchParticipants) {
          sellingZoneBranch.participants.push(
            getSellingZoneParticipant(participant) as SellingZoneParticipant
          );
        }

        sellingZoneData.branchResults.push(sellingZoneBranch);
      }
    }

    return sellingZoneData;
  };

  useEffect(() => {
    if (!detailedAnalysis && sellingZoneRef?.current) {
      sellingZoneRef.current.currentAnimationStep = 0;
      return () => {};
    }

    const onBranchChanged = (ev: CustomEvent<string>) => {
      if (detailedAnalysis?.branch && ev) {
        dispatch(setCurrentBranch(detailedAnalysis?.branch));
        trackEvent('branch-changed');
      }
    };

    const onMessageSelected = (ev: CustomEvent<string>) => {
      dispatch(setCurrentEmail(ev.detail));
      trackEvent('email-changed');
    };

    const onTimelineChanged = (ev: CustomEvent<SellingZoneTimelineChanged>) => {
      dispatch(setGraphItems(ev?.detail?.graphItems || []));
    };

    const onGraphItemSelected = (
      ev: CustomEvent<SellingZoneGraphItemSelected>
    ) => {
      dispatch(setSelectedGraphItem(ev.detail?.id));
    };

    const onAnimationUpdated = (
      ev: CustomEvent<AnalyzeResultElementFlippingType>
    ) => {
      setElementFlippingType(ev.detail);
    };

    const sellingZoneRefCurrent = sellingZoneRef?.current;

    sellingZoneRefCurrent?.addEventListener<'branchChanged'>(
      'branchChanged',
      onBranchChanged,
      false
    );

    sellingZoneRefCurrent?.addEventListener<'messageSelected'>(
      'messageSelected',
      onMessageSelected,
      false
    );

    sellingZoneRefCurrent?.addEventListener<'timelineChanged'>(
      'timelineChanged',
      onTimelineChanged,
      false
    );

    sellingZoneRefCurrent?.addEventListener<'graphItemSelected'>(
      'graphItemSelected',
      onGraphItemSelected,
      false
    );

    sellingZoneRefCurrent?.addEventListener<'animationUpdated'>(
      'animationUpdated',
      onAnimationUpdated,
      false
    );

    if (sellingZoneRefCurrent && detailedAnalysis) {
      sellingZoneRefCurrent.currentAnimationStep = 3;
      sellingZoneRefCurrent.analyzedData = convertSellingZoneData(
        detailedAnalysis
      );

      sellingZoneRefCurrent.curUserInfo = {
        email: user?.email as string,
        name: user?.name as string,
      };

      sellingZoneRefCurrent.internalThreadReadMoreUrl = internalThreadReadMoreUrl;

      sellingZoneRefCurrent.sellingZoneReadMoreUrl = sellingZoneReadMoreUrl;
    }

    return () => {
      sellingZoneRefCurrent?.removeEventListener(
        'branchChanged',
        onBranchChanged,
        true
      );
      sellingZoneRefCurrent?.removeEventListener(
        'messageSelected',
        onMessageSelected,
        true
      );
      sellingZoneRefCurrent?.addEventListener<'timelineChanged'>(
        'timelineChanged',
        onTimelineChanged,
        true
      );
      sellingZoneRefCurrent?.addEventListener<'graphItemSelected'>(
        'graphItemSelected',
        onGraphItemSelected,
        true
      );
      sellingZoneRefCurrent?.addEventListener<'animationUpdated'>(
        'animationUpdated',
        onAnimationUpdated,
        true
      );
    }; // eslint-disable-next-line
  }, [detailedAnalysis?.branch]);

  // eslint-disable-next-line
  // @ts-ignore
  const sellingZoneComponent = <selling-zone-element ref={sellingZoneRef} />;
  let nextGraphItemArrowClickedCounter = 0;
  let prevGraphItemArrowClickedCounter = 0;

  const onNextGraphItemArrowClicked = () => {
    // eslint-disable-next-line
    nextGraphItemArrowClickedCounter++;
    if (sellingZoneRef?.current) {
      sellingZoneRef.current.nextGraphItemArrowClicked = nextGraphItemArrowClickedCounter;
    }
  };

  const onPrevGraphItemArrowClicked = () => {
    // eslint-disable-next-line
    prevGraphItemArrowClickedCounter++;
    if (sellingZoneRef?.current) {
      sellingZoneRef.current.prevGraphItemArrowClicked = prevGraphItemArrowClickedCounter;
    }
  };

  const dataForHeader = {
    participants: selectedBranch?.allBranchParticipants,
    emails: selectedBranch?.emails?.length || 0,
    re: selectedBranch?.subject,
    lastActiveParticipant:
      selectedBranch?.lastActiveParticipant?.metadata.name === user?.name
        ? 'You'
        : selectedBranch?.lastActiveParticipant?.metadata.name,
    sentAt: selectedEmail?.sentAt,
    emailsForDateRange: selectedBranch?.emails,
    userId: user?.userId,
    orgId: user?.orgId,
  };

  const goToLastMessage = () => {
    if (sellingZoneRef?.current) {
      const messageId: string = selectedBranch?.emails
        ? selectedBranch.emails[selectedBranch.emails.length - 1]
            ?.platformMessageId
        : '';

      if (messageId) {
        sellingZoneRef.current.selectedMessageId = messageId;
      }
    }
  };

  const isLoadingData = isLoading.deatiledAnalysis;

  const attrs = {
    wrapper: {
      className: `my-deals flipped--${elementFlippingType}`,
    },

    pageTitle: {
      userId: user?.userId,
      orgId: user?.orgId,
    },

    Header: {
      data: dataForHeader,
      isAnimation: elementFlippingType.includes('change__branch'),
    },

    body: {
      firstColumn: {
        wrapper: {
          className: 'my-deals__first-column',
          style: elementFlippingType.includes('change__branch')
            ? { overflow: 'hidden' }
            : { overflow: 'initial' },
        },

        ProspectData: {
          detailedAnalysis,
          isLoading: isLoadingData,
        },

        CrmData: {
          isLoading: isLoadingData,
        },
      },

      secondColumn: {
        wrapper: {
          className: 'my-deals__second-column',
          style: elementFlippingType.includes('change__branch')
            ? { overflow: 'hidden' }
            : { overflow: 'initial' },
        },

        szRow: {
          wrapper: {
            className: 'my-deals__second-column__sz-row',
          },

          sellingZone: {
            wrapper: {
              className: 'my-deals__second-column__sz-row__selling-zone',
            },

            skeleton: {
              isLoading: isLoadingData,
            },
          },
        },

        divider: {
          className: 'my-deals__second-column__sz-row__divider',
        },

        threadData: {
          wrapper: {
            className: 'my-deals__second-column__thread-data-section',
            style: elementFlippingType.includes('change__branch')
              ? {}
              : { overflow: 'initial' },
          },

          ThreadData: {
            selectedBranch,
            isLoading: isLoadingData,
          },
        },

        emailSection: {
          wrapper: {
            className: 'my-deals__second-column__email-section',
            style:
              elementFlippingType === 'none'
                ? { overflow: 'initial' }
                : { overflow: 'hidden' },
          },

          EmailSection: {
            emailsInBranch: selectedBranch?.emails?.length || 0,
            currentEmail: selectedEmail || null,
            nextMoveText: selectedBranch?.nextMove?.value || '',
            isThreadComplete: detailedAnalysis?.deal?.isCompleted,
            onLeftArrowClick: onPrevGraphItemArrowClicked,
            onRightArrowClick: onNextGraphItemArrowClicked,
            selectedGraphItem,
            goToLastMessage,
            isLoading: isLoadingData,
            firstReplyIndex,
          },
        },
      },
    },
  };

  return (
    <div {...attrs.wrapper}>
      <SampleDataLabel />
      <MyEngagementsTitle {...attrs.pageTitle} />
      <DetailedViewHeader {...attrs.Header} />
      <div {...attrs.body.firstColumn.wrapper}>
        <ProspectData {...attrs.body.firstColumn.ProspectData} />
        <CrmData {...attrs.body.firstColumn.CrmData} />
      </div>
      <div {...attrs.body.secondColumn.wrapper}>
        <div {...attrs.body.secondColumn.szRow.wrapper}>
          <div {...attrs.body.secondColumn.szRow.sellingZone.wrapper}>
            <SellingZoneSkeleton
              {...attrs.body.secondColumn.szRow.sellingZone.skeleton}
            >
              {sellingZoneComponent}
            </SellingZoneSkeleton>
          </div>
          <div {...attrs.body.secondColumn.divider} />
          <div {...attrs.body.secondColumn.threadData.wrapper}>
            <ThreadData {...attrs.body.secondColumn.threadData.ThreadData} />
          </div>
        </div>
        <div {...attrs.body.secondColumn.emailSection.wrapper}>
          <EmailSection
            {...attrs.body.secondColumn.emailSection.EmailSection}
          />
        </div>
      </div>
    </div>
  );
};
